import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  FormLabel,
  FormControlLabel,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Badge,
  useTheme
} from "@mui/material";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Custom Component Imports
import CustomTextField from "../../../components/mui/text-field";
import CustomChip from "../../../components/mui/chip";
import CustomAutocomplete from "components/mui/autocomplete";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const tagList = ["Senior", "Junior", "Intern", "On-call"];

const AdvancedFilter = ({hasSearchPermission}) => {
  const theme = useTheme();

  // ** States
  const [openReqDetail, setOpenReqDetail] = useState(false);
  const [openCusInfo, setOpenCusInfo] = useState(false);
  const [openJobsiteNPropInfo, setOpenJobsiteNPropInfo] = useState(false);
  const [openServiceDetail, setOpenServiceDetail] = useState(false);
  const [openTagNLinkRecord, setOpenTagNLinkRecord] = useState(false);
  const [tags, setTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleTagChange = (event) => {
    const value = event.target.value;
    setTags(typeof value === "string" ? value.split(",") : value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleApplyFilter = () => {
    setOpen(false);
    setIsFiltered(true);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* <Badge 
          badgeContent={<Typography variant='body2' sx={{ p:0, m:0, color:'white' }} 
          onClick={() => console.log('Reset Filter!')}
        >
          X
        </Typography>} 
          color='error' 
          sx={{ cursor: 'pointer' }}
        >
          <Button variant='outlined' onClick={()=> setOpen(true)} startIcon={<Icon icon='tabler:filter' />}>Filter</Button>
        </Badge> */}
        <Button 
          variant='outlined'
          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
          disabled={!hasSearchPermission}
          {...(hasSearchPermission && {
            onClick: ()=> setOpen(true)
          })}
          startIcon={<Icon icon='tabler:filter' />}
        >
          Filter
        </Button>
        <Dialog
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 24px",
            }}
          >
            <Typography variant="h3">Advanced Filter</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                0 requests match your criteria
              </Typography>
              <Tooltip title='You don’t need to input exact details. A part of the name, address, or phone number is enough to proceed.'>
                <IconButton color='info'>
                  <Icon icon='tabler:info-circle' />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenReqDetail(!openReqDetail)}
                  >
                    <Icon
                      icon={
                        openReqDetail
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Request Details
                          </Typography>
                          <CustomChip label="0" skin="light" color="success" />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openReqDetail} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Request Title"
                        placeholder="Fence Installation"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Request Source"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Request Priority"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Preferred Times"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Status"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            mb: "4px",
                            color: "#2F2B3DC7",
                          }}
                        >
                          Complete Work By
                        </FormLabel>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 5,
                          }}
                        >
                          <DatePicker
                            label="From"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                          <DatePicker
                            label="To"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            mb: "4px",
                            color: "#2F2B3DC7",
                          }}
                        >
                          Request Date
                        </FormLabel>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 5,
                          }}
                        >
                          <DatePicker
                            label="From"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                          <DatePicker
                            label="To"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            mb: "4px",
                            color: "#2F2B3DC7",
                          }}
                        >
                          Due Date
                        </FormLabel>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 5,
                          }}
                        >
                          <DatePicker
                            label="From"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                          <DatePicker
                            label="To"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton onClick={() => setOpenCusInfo(!openCusInfo)}>
                    <Icon
                      icon={
                        openCusInfo
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Customer Information
                          </Typography>
                          <CustomChip label="0" skin="light" color="success" />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openCusInfo} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Full Name"
                        placeholder="John Doe"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Company Name"
                        placeholder="Cleri"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Customer Type"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Phone Number"
                        placeholder="000 000 0000"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Email"
                        placeholder="john@example.com"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Address"
                        placeholder="123 street, main road"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Billing Address"
                        placeholder="123 street, main road"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete 
                        fullWidth
                        options={[]}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            placeholder='Select'
                            label="Assigned To"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() =>
                      setOpenJobsiteNPropInfo(!openJobsiteNPropInfo)
                    }
                  >
                    <Icon
                      icon={
                        openJobsiteNPropInfo
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Jobsite and Property Information
                          </Typography>
                          <CustomChip label="0" skin="light" color="success" />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={openJobsiteNPropInfo}
                  timeout="auto"
                  unmountOnExit
                >
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Jobsite Name"
                        placeholder="Warehouse"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Jobsite Type"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete 
                        fullWidth
                        options={[]}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            placeholder='Select'
                            label="Jobsite Contact"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Property Area Name"
                        placeholder="Back Yard"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Property Area Type"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenServiceDetail(!openServiceDetail)}
                  >
                    <Icon
                      icon={
                        openServiceDetail
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Service Details
                          </Typography>
                          <CustomChip label="0" skin="light" color="success" />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openServiceDetail} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete 
                        fullWidth
                        options={[]}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            placeholder='Select'
                            label="Service"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Description"
                        placeholder="Enter the description to match"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomTextField
                        fullWidth
                        label="Special Instructions"
                        placeholder="Be careful of the flowerbed"
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenTagNLinkRecord(!openTagNLinkRecord)}
                  >
                    <Icon
                      icon={
                        openTagNLinkRecord
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Tags and Linked Records
                          </Typography>
                          <CustomChip label="0" skin="light" color="success" />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openTagNLinkRecord} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Tags"
                        placeholder="e.g., On-call, Senior, Junior"
                        SelectProps={{
                          MenuProps,
                          displayEmpty: true,
                          multiple: true,
                          value: tags,
                          onChange: (e) => handleTagChange(e),
                          renderValue: (selected) =>
                            selected.length === 0
                              ? "Select Tags"
                              : selected.join(", "),
                        }}
                      >
                        {tagList.map((tag) => (
                          <MenuItem key={tag} value={tag}>
                            {tag}
                          </MenuItem>
                        ))}
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Record Type"
                        defaultValue="select"
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "20px 24px !important",
            }}
            className="dialog-actions-dense"
          >
            <Box>
              <Button
                variant="contained"
                color="info"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 4,
              }}
            >
              <Button variant="tonal" color="primary">
                Reset
              </Button>
              <Button
                variant="contained"
                color="main"
                onClick={handleApplyFilter}
              >
                Apply
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancedFilter;
